import restClient from "api/restClient";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import SimpleNotification from "components/Notification/SimpleNotification";
import { FeConfigType } from "config/config";
import { ActionData } from "containers/Resources/components/CommonActions/Action";
import { forceRecordUpdate } from "containers/Resources/components/CommonActions/CommonActions";
import { Record, RecordType } from "graphql/generated/graphql";
import { cloneDeep } from "lodash";
import { Dispatch } from "redux";
import { getTypedLowerCase } from "utils/service";

interface GitLogData {
    bundle_id: string | null;
    id?: string;
    identity_id: string | null;
    message: string;
    name: string;
    project_id: string | null;
    record_id: string | null;
    record_type?: RecordType;
    timestamp: string;
}

interface GroupedLogListData {
    [locationId: string]: GitLogData[];
}

interface GroupedByNameLogData {
    [name: string]: GitLogData[];
}

export interface UncommittedData extends GitLogData {
    idList: string[];
}

interface UncommittedDataGroupedByLocation {
    [location: string]: UncommittedDataGroupedByName;
}

export interface UncommittedDataGroupedByName {
    [name: string]: UncommittedData;
}

const workspacePath = "workspace";

export const groupGitLogDataByLocation = (data: GitLogData[]) =>
    data.reduce((acc: GroupedLogListData, currentData: GitLogData) => {
        const location = currentData.project_id ?? currentData.bundle_id ?? workspacePath;
        acc[location] = [...(acc[location] ?? []), currentData];
        return acc;
    }, {});

export const groupGitLogDataByName = (
    data: GroupedLogListData
): { [location: string]: GroupedByNameLogData } => {
    const result = {};
    for (const locationKey in data) {
        const groupedGitLogByName = data[locationKey].reduce(
            (acc: GroupedByNameLogData, currentLogData: GitLogData) => {
                const name = currentLogData.name;
                acc[name] = [...(acc[name] ?? []), currentLogData];
                return acc;
            },
            {}
        );
        result[locationKey] = groupedGitLogByName;
    }
    return result;
};

export const getAllUncommittedGitLogData = (data: GitLogData[]) => {
    const groupedGitLogDataByLocation = groupGitLogDataByLocation(data);
    const groupedGitLogDataByName = groupGitLogDataByName(groupedGitLogDataByLocation);
    const uncommittedData: UncommittedDataGroupedByLocation = {};

    for (const locationKey in groupedGitLogDataByName) {
        const uncommitted = prepareUnstageData(groupedGitLogDataByName[locationKey]);
        uncommittedData[locationKey] = uncommitted;
    }

    return uncommittedData;
};

export const isContainerType = (type: FeConfigType) => {
    const resourceType = getTypedLowerCase(type);
    return resourceType === "bundle" || resourceType === "project" || resourceType === "workspace";
};

export const getRecordLocation = (resourceData: Record) =>
    resourceData.project_id ? "project" : resourceData.bundle_id ? "bundle" : "workspace";

export const getLocationIdPath = (data: ActionData) => {
    if (isContainerType(data.resourceType)) {
        return `${data.resourceType}/${data.resourceData.id}`;
    }
    const location = getRecordLocation(data.resourceData);
    return `${location}/${data.resourceData[`${location}_id_${location}`].id}`;
};

export const generateMessage = (data) => {
    const sortedByDate = ascendingSortByDate([...cloneDeep(data)], (item) => item.timestamp);
    const messageLinesArray = sortedByDate.reduce((acc, item) => {
        if (item.message) {
            const messageLines = item.message.split("\n");
            acc.push(...messageLines);
        }
        return acc;
    }, []);
    if (messageLinesArray.length <= 36) {
        return messageLinesArray.join("\n");
    }

    const first = messageLinesArray.slice(0, 18);
    const last = messageLinesArray.slice(-18);

    return [...first, "\n[...lines are truncated...]\n", ...last].join("\n");
};

const sortDate = <T extends {}>(elems: T[], sortGetter, oldestShownFirst): T[] =>
    elems.sort((a, b) => {
        const valueA = Date.parse(sortGetter(a));
        const valueB = Date.parse(sortGetter(b));
        if (oldestShownFirst) {
            if (valueA < valueB) return -1;
            if (valueA > valueB) return 1;
        } else {
            if (valueA > valueB) return -1;
            if (valueA < valueB) return 1;
        }
        return 0;
    });
/**
 * more recent date is the first element of the array
 */
export const descendingSortByDate = <T extends {}>(elems: T[], sortGetter): T[] =>
    sortDate(elems, sortGetter, false);

/**
 * oldest date is the first element of the array
 */
export const ascendingSortByDate = <T extends {}>(elems: T[], sortGetter): T[] =>
    sortDate(elems, sortGetter, true);
const prepareUnstageData = (data: GroupedByNameLogData) => {
    const clonedData = cloneDeep(data);
    const unstagedData: UncommittedDataGroupedByName = {};

    for (const key in clonedData) {
        const lastModifiedRecordEntry = descendingSortByDate(
            [...clonedData[key]],
            (record) => record.timestamp
        )[0];
        const idList = clonedData[key].map((record) => record.id);
        delete lastModifiedRecordEntry.id;
        lastModifiedRecordEntry["idList"] = idList;
        lastModifiedRecordEntry["message"] = generateMessage(clonedData[key]);
        unstagedData[key] = lastModifiedRecordEntry as UncommittedData;
    }
    return unstagedData;
};

interface BulkActionProps {
    formData: {
        commit_message: string;
        records: { name: string }[];
    };
    actionData: ActionData;
    closeModal?: () => void;
    dispatch?: Dispatch;
}

interface BulkCommitProps extends BulkActionProps {
    uncommittedData: UncommittedDataGroupedByName;
    path?: string;
    resourcePath?: string;
}

const bulkCommitPath = "bulk_commit";

export const bulkCommit = ({
    formData,
    actionData,
    closeModal,
    uncommittedData,
    path = bulkCommitPath,
    resourcePath,
}: BulkCommitProps) => {
    const locationIdPath = resourcePath ?? getLocationIdPath(actionData);
    const records = formData.records.map((record) => record.name);
    const idList = [] as string[];

    records.forEach((record) => {
        idList.push(...uncommittedData[record].idList);
    });
    const gitLogIds = idList.length ? { git_log_ids: idList } : {};
    const message = formData.commit_message.length ? { message: formData.commit_message } : {};

    restClient
        .patch(
            `${locationIdPath}/git/${path}`,
            {
                ...gitLogIds,
                ...message,
            },
            {
                params: {
                    allow_deleted: true,
                    async: true,
                },
            }
        )
        .then(() => {
            NotificationService.success(
                <SimpleNotification
                    title={`${path === bulkCommitPath ? "Bulk commit" : "Commit"} was started`}
                />
            );
        })
        .then(() => actionData?.afterAction?.())
        .catch((error) =>
            NotificationService.error(
                <RestErrorNotification
                    titleOverride={`Sorry, ${path.replace(/_/g, " ")} failed`}
                    error={error}
                />
            )
        )
        .finally(() => closeModal?.());
};

export const bulkReset = ({ formData, actionData, dispatch }: BulkActionProps) => {
    const locationIdPath = getLocationIdPath(actionData);
    const records = formData.records.map((record) => record.name);

    return restClient
        .patch(`${locationIdPath}/git/bulk_restore`, records, {
            params: {
                allow_deleted: true,
            },
        })
        .then(() => {
            NotificationService.success(
                <SimpleNotification title="Successfully reset to the git status." />
            );
        })
        .then(() => forceRecordUpdate(dispatch))
        .catch((error) => {
            NotificationService.error(<RestErrorNotification error={error} />);
            throw error;
        });
};

export const getUncommittedDataPath = (resourceData: Record) => {
    const recordType = resourceData?.record_type?.toLowerCase();

    return recordType === "workspace"
        ? workspacePath
        : recordType === "project" || recordType === "bundle"
        ? resourceData.id
        : resourceData.project_id ?? resourceData.bundle_id ?? workspacePath;
};

export const getUncommittedGitLogData = ({
    allUncommittedData,
    resourceData,
}: {
    allUncommittedData: UncommittedDataGroupedByLocation;
    resourceData?: Record;
}) => {
    const path = resourceData ? getUncommittedDataPath(resourceData) : null;
    const uncommittedDataByPath = path ? allUncommittedData[path] : {};

    return uncommittedDataByPath;
};

export const isGitActionButtonDisabled = (data: ActionData) =>
    Object.values(data?.uncommittedData ?? {}).length === 0;

export const getUncommittedChangesCount = (uncommittedData: UncommittedDataGroupedByName) => {
    const uncommittedChangesCount = Object.keys(uncommittedData ?? {}).length;

    return uncommittedChangesCount;
};
